import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import ScoreBars from "@/components/ScoreBars";
import { BookMarked, Search, Loader2, ArrowLeft, BookmarkPlus, ScanText, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function Dictionary() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [popular, setPopular] = useState(null);
  const [entry, setEntry] = useState(null);
  const [busy, setBusy] = useState(false);
  const [safety, setSafety] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/dictionary/popular").then((r) => setPopular(r.data.items || [])).catch(() => setPopular([])); }, []);

  const search = async (term) => {
    const word = (term ?? q).trim();
    if (!word) return;
    setBusy(true); setSafety(null); setEntry(null);
    try {
      const r = await api.get(`/dictionary/search?q=${encodeURIComponent(word)}`);
      if (r.data.safety) { setSafety(r.data.safety); return; }
      setEntry(r.data.entry);
      setQ(word);
    } catch (e) { toast.error("Search failed"); } finally { setBusy(false); }
  };

  const saveToVault = async () => {
    if (!entry) return;
    setSaving(true);
    try {
      await api.post("/vault", { word: entry.word, decode_type: "Personal Identity" });
      toast.success("Saved to Vault");
    } catch (e) { toast.error("Could not save"); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-display font-semibold flex items-center gap-2"><BookMarked className="w-6 h-6 green-text" />Litrametry Dictionary</h1>
        <p className="text-[var(--bone-muted)] text-sm">Search any word for an instant, deterministic decode — root, seal, archetype, and signals.</p>
      </div>

      <div className="litra-card p-4">
        <div className="flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="Search a word, name, or brand…" className="bg-white/[0.04] border-white/[0.1]" data-testid="dictionary-search-input" />
          <Button onClick={() => search()} disabled={busy} className="bg-[var(--primary)] hover:opacity-90" data-testid="dictionary-search-button">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {safety && (
        <div className="litra-card p-5 flex items-start gap-3" style={{ borderColor: "rgba(194,75,75,0.4)" }} data-testid="dictionary-safety">
          <ShieldAlert className="w-5 h-5 text-[#C24B4B] shrink-0 mt-0.5" />
          <p className="text-sm text-[var(--bone-muted)]">{safety.message}</p>
        </div>
      )}

      {entry ? (
        <div className="space-y-4 fade-in" data-testid="dictionary-entry">
          <button onClick={() => setEntry(null)} className="text-sm text-[var(--bone-faint)] hover:text-[var(--bone-muted)] flex items-center gap-1.5" data-testid="dictionary-back">
            <ArrowLeft className="w-4 h-4" /> Back to dictionary
          </button>
          <div className="litra-card p-6" style={{ borderColor: "rgba(20,90,67,0.4)" }}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-3xl font-display font-semibold">{entry.word}</div>
                <div className="text-sm text-[var(--bone-faint)] mt-1">{entry.root_letter} · {entry.root} → {entry.seal_letter} · {entry.seal}</div>
              </div>
              <Badge className="gold-edge gold-text bg-transparent" data-testid="dictionary-archetype">{entry.archetype}</Badge>
            </div>
            <p className="mt-3 text-[var(--bone-muted)] leading-relaxed">{entry.overview}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Badge variant="outline" className="border-[rgba(185,192,204,0.3)]">{entry.word_class}</Badge>
              {entry.top_letter_signals?.slice(0, 3).map((s, i) => (
                <span key={i} className="glass rounded-full px-3 py-1 mono-chip"><span className="green-text font-semibold">{s.letter}</span> {s.technical_name}</span>
              ))}
            </div>
            <div className="mt-5">
              <div className="mono-chip text-[var(--bone-faint)] mb-3">13 SCORING CATEGORIES</div>
              <ScoreBars scores={entry.scores} />
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <Button onClick={saveToVault} disabled={saving} variant="outline" className="glass" data-testid="dictionary-save-vault">
                <BookmarkPlus className="w-4 h-4 mr-2" /> Save to Vault
              </Button>
              <Button onClick={() => navigate("/decode")} className="bg-[var(--primary)] hover:opacity-90" data-testid="dictionary-open-decode">
                <ScanText className="w-4 h-4 mr-2" /> Full decode
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="mono-chip text-[var(--bone-faint)] mb-3">POPULAR WORDS</div>
          {popular === null ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24 w-full bg-white/[0.04]" />)}</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="dictionary-popular">
              {popular.map((w) => (
                <button key={w.word} onClick={() => search(w.word)} data-testid={`dictionary-word-${w.word.toLowerCase()}`}
                  className="litra-card litra-card-hover p-4 text-left">
                  <div className="flex items-center justify-between">
                    <div className="text-lg font-display font-semibold">{w.word}</div>
                    <span className="text-xs gold-text">{w.archetype}</span>
                  </div>
                  <div className="text-xs text-[var(--bone-faint)] mt-1">{w.root} → {w.seal}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {w.top_signals?.slice(0, 2).map((s, i) => (
                      <span key={i} className="mono-chip glass rounded-full px-2 py-0.5 text-[10px]">{s.label} {s.value}</span>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
